import { FileText } from 'lucide-react';

export default function TermsOfUse() {
  return (
    <div data-testid="terms-of-use-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-meritum-navy via-meritum-teal to-meritum-teal">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="inline-block px-4 py-2 bg-white/10 border border-white/20 rounded-sm text-meritum-gold font-body text-sm font-medium mb-6">
            Legal
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight leading-none text-white mb-4">
            Terms of Use
          </h1>
          <p className="font-body text-base text-slate-200 max-w-2xl mx-auto">
            Please read these terms carefully before using the MeritumPraxis platform.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="prose prose-slate max-w-none">
            {/* Important Notice */}
            <div className="bg-meritum-gold/10 border-l-4 border-meritum-gold p-6 rounded-r-lg mb-12">
              <div className="flex items-start gap-4">
                <FileText className="text-meritum-gold flex-shrink-0 mt-1" size={24} />
                <div>
                  <h2 className="font-heading text-xl font-semibold text-meritum-navy mt-0 mb-2">
                    Agreement to Terms
                  </h2>
                  <p className="font-body text-slate-600 mb-0">
                    By accessing and using MeritumPraxis, you agree to be bound by these Terms of Use. 
                    If you do not agree, please do not use the platform.
                  </p>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  1. User Responsibilities
                </h2>
                <p className="font-body text-slate-600 leading-relaxed mb-4">
                  Users agree to:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Provide accurate information:</strong> All information submitted to the platform 
                      must be truthful, accurate, and not misleading.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Use the platform responsibly:</strong> Users must not engage in any activity 
                      that could harm, disable, or impair the platform or other users' experience.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-meritum-teal rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">
                      <strong>Avoid misuse of communication features:</strong> The messaging system must be 
                      used for legitimate professional communication only.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  2. Account Conduct
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  Users are responsible for maintaining the confidentiality of their account credentials. 
                  Any activity occurring under your account is your responsibility. You must notify 
                  MeritumPraxis immediately of any unauthorized use of your account.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  3. Verification Process
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  The verification process is designed to ensure credibility and trust on the platform. 
                  Users agree to participate honestly in verification requests and to provide authentic 
                  documentation when requested. Falsifying information or manipulating the verification 
                  process is strictly prohibited.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  4. Prohibited Activities
                </h2>
                <p className="font-body text-slate-600 leading-relaxed mb-4">
                  Users must not:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-red-500 rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Submit false or misleading information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-red-500 rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Impersonate others or misrepresent affiliations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-red-500 rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Use the platform for spam or unsolicited communications</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-red-500 rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Attempt to circumvent security measures</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 mt-2 bg-red-500 rounded-full flex-shrink-0" />
                    <span className="font-body text-slate-600">Engage in harassment or discriminatory behavior</span>
                  </li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  5. Account Suspension
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  MeritumPraxis may suspend or terminate accounts that violate platform policies. 
                  This includes, but is not limited to, accounts found to have submitted false information, 
                  engaged in prohibited activities, or misused platform features. Users may appeal 
                  suspension decisions by contacting support.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  6. Intellectual Property
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  The MeritumPraxis platform, including its design, features, and content (excluding user-submitted content), 
                  is the intellectual property of MeritumPraxis (Pty) Ltd. Users retain ownership of content 
                  they submit but grant MeritumPraxis a license to use such content for platform operations.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  7. Limitation of Liability
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  MeritumPraxis is provided "as is" without warranties of any kind. We are not liable for 
                  any damages arising from the use of the platform, including but not limited to employment 
                  outcomes, business decisions, or loss of data.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  8. Changes to Terms
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  MeritumPraxis reserves the right to modify these Terms of Use at any time. Users will be 
                  notified of significant changes. Continued use of the platform after changes constitutes 
                  acceptance of the updated terms.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  9. Governing Law
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  These Terms of Use are governed by the laws of the Republic of South Africa. Any disputes 
                  arising from these terms or the use of the platform shall be subject to the exclusive 
                  jurisdiction of the courts of the Republic of South Africa.
                </p>
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold text-meritum-navy mb-4">
                  10. Contact
                </h2>
                <p className="font-body text-slate-600 leading-relaxed">
                  For questions about these Terms of Use, please contact us at:{' '}
                  <a href="mailto:info@meritumpraxis.co.za" className="text-meritum-teal hover:underline">
                    info@meritumpraxis.co.za
                  </a>
                </p>
              </div>
            </div>

            {/* Last Updated */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="font-body text-sm text-slate-500">
                <strong>Effective Date:</strong> February 2026<br />
                <strong>Company:</strong> MeritumPraxis (Pty) Ltd<br />
                <strong>Jurisdiction:</strong> Republic of South Africa
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
